import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Database, Play, CheckCircle, AlertCircle, Info, ExternalLink, Clock, Zap, Key, Download, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AutomationManager } from "./AutomationManager";

export const UsdaReportProcessor = () => {
  const [processing, setProcessing] = useState(false);
  const [apiStatus, setApiStatus] = useState<'unknown' | 'available' | 'auth-required' | 'unavailable' | 'rate-limited'>('unknown');
  const [cacheInfo, setCacheInfo] = useState<any>(null);
  const [lastRequestCached, setLastRequestCached] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [workflowStep, setWorkflowStep] = useState<'idle' | 'storing' | 'matching'>('idle');
  const { toast } = useToast();

  const checkApiStatus = async () => {
    try {
      console.log('🔍 Checking USDA MARS API status...');
      const { data: response, error } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          endpoint: 'prices',
          test: true
        }
      });

      console.log('API Status Response:', response);

      if (error) {
        console.error('API Status Check Error:', error);
        setApiStatus('unavailable');
        setIsAuthenticated(false);
        return;
      }

      if (response?.rateLimited) {
        setApiStatus('rate-limited');
        setIsAuthenticated(false);
      } else if (response?.authenticationRequired) {
        setApiStatus('auth-required');
        setIsAuthenticated(false);
      } else if (response?.success) {
        setApiStatus('available');
        setIsAuthenticated(response?.authenticated || false);
        console.log('✅ USDA MARS API is accessible and authenticated');
      } else {
        setApiStatus('unavailable');
        setIsAuthenticated(false);
      }

      // Update cache info if available
      if (response?.cacheInfo) {
        setCacheInfo(response.cacheInfo);
      }
    } catch (err) {
      console.error('Error checking API status:', err);
      setApiStatus('unavailable');
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    // Check API status when component mounts
    checkApiStatus();
  }, []);

  const storeAllReports = async () => {
    if (apiStatus === 'auth-required') {
      toast({
        title: "API Authentication Required",
        description: "The USDA MARS API requires authentication. Please contact the administrator to configure API access.",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    setWorkflowStep('storing');

    try {
      console.log('📥 Starting reports storage workflow...');
      
      const { data: response, error } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          storeReports: true
        }
      });

      if (error) {
        console.error('Error storing reports:', error);
        toast({
          title: "Storage Failed",
          description: "Failed to store USDA reports. Please try again.",
          variant: "destructive"
        });
        return;
      }

      if (!response?.success) {
        console.error('Reports storage error:', response?.error);
        toast({
          title: "Storage Error",
          description: response?.error || 'Unknown error occurred',
          variant: "destructive"
        });
        return;
      }

      console.log('✅ Reports stored successfully:', response);
      
      toast({
        title: "Reports Stored Successfully!",
        description: `${response.message}. Fetched: ${response.data?.totalFetched}, Stored: ${response.data?.totalStored}`,
        variant: "default"
      });

      // Update cache info
      if (response.cacheInfo) {
        setCacheInfo(response.cacheInfo);
      }
      
    } catch (err) {
      console.error('Error in reports storage workflow:', err);
      toast({
        title: "Storage Failed",
        description: "Failed to store USDA reports. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
      setWorkflowStep('idle');
    }
  };

  const matchAllProfiles = async () => {
    setProcessing(true);
    setWorkflowStep('matching');

    try {
      console.log('🎯 Starting profile matching workflow...');
      
      const { data: response, error } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          matchProfiles: true
        }
      });

      if (error) {
        console.error('Error matching profiles:', error);
        toast({
          title: "Matching Failed",
          description: "Failed to match reports to user profiles. Please try again.",
          variant: "destructive"
        });
        return;
      }

      if (!response?.success) {
        console.error('Profile matching error:', response?.error);
        toast({
          title: "Matching Error",
          description: response?.error || 'Unknown error occurred',
          variant: "destructive"
        });
        return;
      }

      console.log('✅ Profile matching completed:', response);
      
      toast({
        title: "Profile Matching Completed!",
        description: response.message,
        variant: "default"
      });

      // Update cache info
      if (response.cacheInfo) {
        setCacheInfo(response.cacheInfo);
      }
      
    } catch (err) {
      console.error('Error in profile matching workflow:', err);
      toast({
        title: "Matching Failed",
        description: "Failed to match reports to user profiles. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
      setWorkflowStep('idle');
    }
  };

  const fetchPriceData = async () => {
    if (apiStatus === 'auth-required') {
      toast({
        title: "API Authentication Required",
        description: "The USDA MARS API requires authentication. Please contact the administrator to configure API access.",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);

    try {
      console.log('🚀 Fetching USDA price data...');
      
      const { data: usdaResponse, error } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          endpoint: 'prices'
        }
      });

      if (error) {
        console.error('Error calling USDA edge function:', error);
        
        if (error.message?.includes('Rate limit exceeded')) {
          setApiStatus('rate-limited');
          toast({
            title: "Rate Limit Exceeded",
            description: "Too many requests to the USDA API. Please wait before trying again.",
            variant: "destructive"
          });
        } else if (error.message?.includes('Authentication Required')) {
          setApiStatus('auth-required');
          setIsAuthenticated(false);
          toast({
            title: "Authentication Failed",
            description: "The MARS API key may be invalid or expired. Please check the configuration.",
            variant: "destructive"
          });
        }
        return;
      }

      if (!usdaResponse?.success) {
        console.error('USDA API error:', usdaResponse?.error);
        const errorMsg = usdaResponse?.error || 'Unknown error';
        
        if (errorMsg.includes('Rate limit exceeded')) {
          setApiStatus('rate-limited');
        } else if (errorMsg.includes('Authentication Required')) {
          setApiStatus('auth-required');
          setIsAuthenticated(false);
        }
        
        toast({
          title: "API Error",
          description: errorMsg,
          variant: "destructive"
        });
        return;
      }

      // Update authentication status
      setIsAuthenticated(usdaResponse.authenticated || false);

      // Update cache and rate limit info
      setLastRequestCached(usdaResponse.cached || false);
      if (usdaResponse.cacheInfo) {
        setCacheInfo(usdaResponse.cacheInfo);
      }

      const priceData = usdaResponse.data || [];
      console.log(`✅ Fetched ${priceData.length} price records...`);

      const cacheStatus = lastRequestCached ? " (from cache)" : "";
      const authStatus = isAuthenticated ? " with authentication" : "";
      toast({
        title: "Success!",
        description: `Fetched ${priceData.length} price records${cacheStatus}${authStatus}.`,
        variant: "default"
      });
      
      setApiStatus('available');

    } catch (err) {
      console.error('Error fetching USDA price data:', err);
      
      toast({
        title: "Fetch Failed",
        description: "Failed to fetch USDA price data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = () => {
    switch (apiStatus) {
      case 'available':
        return (
          <div className="flex items-center space-x-2">
            <Badge variant="secondary" className="bg-green-100 text-green-800">API Available</Badge>
            {isAuthenticated && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-800 flex items-center space-x-1">
                <Key size={12} />
                <span>Authenticated</span>
              </Badge>
            )}
          </div>
        );
      case 'auth-required':
        return <Badge variant="destructive">Authentication Required</Badge>;
      case 'rate-limited':
        return <Badge variant="destructive" className="bg-orange-100 text-orange-800">Rate Limited</Badge>;
      case 'unavailable':
        return <Badge variant="destructive">API Unavailable</Badge>;
      default:
        return <Badge variant="outline">Checking...</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Automation Manager */}
      <AutomationManager />
      
      {/* Original USDA Report Processor */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-green-800 flex items-center space-x-2">
            <Database size={20} />
            <span>Manual USDA Data Operations</span>
            {getStatusBadge()}
            {lastRequestCached && (
              <Badge variant="outline" className="bg-blue-100 text-blue-800 flex items-center space-x-1">
                <Zap size={12} />
                <span>Cached</span>
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* API Status Check */}
          <div className="flex items-center space-x-4">
            <Button 
              onClick={checkApiStatus}
              variant="outline"
              size="sm"
              disabled={processing}
              className="flex items-center space-x-2"
            >
              <CheckCircle size={14} />
              <span>Check API Status</span>
            </Button>
          </div>

          {/* Workflow Steps */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center space-x-2">
              <Info size={16} />
              <span>USDA Reports Workflow</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Step 1: Store Reports */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                  <span className="font-medium">Store All Reports</span>
                </div>
                <p className="text-sm text-gray-600 ml-8">
                  Fetch all USDA reports and store with extracted keywords (commodities, varieties, market cities)
                </p>
                <div className="ml-8">
                  <Button 
                    onClick={storeAllReports} 
                    disabled={processing || apiStatus === 'auth-required' || workflowStep === 'storing'}
                    className="flex items-center space-x-2"
                    size="sm"
                  >
                    <Download size={14} />
                    <span>{workflowStep === 'storing' ? 'Storing Reports...' : 'Store All Reports'}</span>
                  </Button>
                </div>
              </div>

              {/* Step 2: Match Profiles */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                  <span className="font-medium">Match User Profiles</span>
                </div>
                <p className="text-sm text-gray-600 ml-8">
                  Match stored reports to all user profiles based on location and crop preferences
                </p>
                <div className="ml-8">
                  <Button 
                    onClick={matchAllProfiles} 
                    disabled={processing || workflowStep === 'matching'}
                    variant="outline"
                    className="flex items-center space-x-2"
                    size="sm"
                  >
                    <Users size={14} />
                    <span>{workflowStep === 'matching' ? 'Matching Profiles...' : 'Match All Profiles'}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Regular Data Fetching */}
          <div className="border-t pt-4">
            <h3 className="font-semibold text-gray-700 mb-3">Regular Data Fetching</h3>
            <div className="flex items-center space-x-4">
              <Button 
                onClick={fetchPriceData} 
                disabled={processing || apiStatus === 'auth-required'}
                className="flex items-center space-x-2"
              >
                <Play size={16} />
                <span>{processing && workflowStep === 'idle' ? 'Fetching...' : 'Fetch USDA Price Data'}</span>
              </Button>
            </div>
          </div>

          {/* Authentication Status */}
          {apiStatus === 'available' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <CheckCircle size={16} className="text-green-600 mt-0.5" />
                <div className="text-sm text-green-700">
                  <p className="font-medium mb-1">MARS API Connected Successfully</p>
                  <p>
                    ✅ Authentication: {isAuthenticated ? 'Active' : 'Not Required'}<br/>
                    ✅ API Access: Verified<br/>
                    ✅ Rate Limiting: Active<br/>
                    ✅ Caching: Enabled
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cache Information */}
          {cacheInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Clock size={16} className="text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-2">Cache Status</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs mb-1"><strong>Cache Entries:</strong> {cacheInfo.size}</p>
                      <p className="text-xs"><strong>Last Request:</strong> {lastRequestCached ? 'From Cache' : 'Fresh API Call'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-1">Active Cache Keys:</p>
                      {cacheInfo.keys.length > 0 ? (
                        <div className="text-xs space-y-1">
                          {cacheInfo.keys.map((key: string, index: number) => (
                            <p key={index} className="text-xs text-blue-600">• {key}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">No cached data</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {apiStatus === 'rate-limited' && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle size={16} className="text-orange-600 mt-0.5" />
                <div className="text-sm text-orange-800">
                  <p className="font-medium mb-2">Rate Limit Exceeded</p>
                  <p className="mb-2">
                    Too many requests have been made to the USDA API. The system implements intelligent caching to reduce API calls.
                  </p>
                  <p className="text-xs">Please wait a few minutes before trying again, or check if cached data is sufficient for your needs.</p>
                </div>
              </div>
            </div>
          )}

          {apiStatus === 'auth-required' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle size={16} className="text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-2">USDA MARS API Authentication Required</p>
                  <p className="mb-2">
                    The USDA MARS API requires authentication. The API key may be invalid or expired.
                  </p>
                  <a 
                    href="https://marsapi.ams.usda.gov/services/help"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-yellow-700 hover:text-yellow-900 underline"
                  >
                    <span>Visit USDA MARS API Help</span>
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </div>
          )}

          {processing && (
            <div className="flex items-center space-x-2 text-sm text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>
                {workflowStep === 'storing' && 'Storing USDA reports with keywords...'}
                {workflowStep === 'matching' && 'Matching reports to user profiles...'}
                {workflowStep === 'idle' && 'Processing request...'}
              </span>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Info size={16} className="text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Workflow Features:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Smart Keyword Extraction:</strong> Automatically extracts commodities, varieties, and market cities</li>
                  <li><strong>Bulk Storage:</strong> Stores all reports with structured data for easy querying</li>
                  <li><strong>Profile Matching:</strong> Matches reports to users based on location and crop preferences</li>
                  <li><strong>City Mapping:</strong> Maps user locations to nearest market cities</li>
                  <li><strong>Relevance Scoring:</strong> Calculates relevance scores for personalized report recommendations</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
